import { useCallback, useRef } from 'react';

type OnUpdateFunction<T> = (key: string, handler: null | ((newTimestamp: T) => void)) => void;

type RefStateCurrent<T> = {
  value: T;
  onUpdate: OnUpdateFunction<T>;
};

export interface RefState<T> extends Omit<React.MutableRefObject<T>, 'current'> {
  current: RefStateCurrent<T>;
}

const useRefState = <T>(initialValue: T): [RefState<T>, (arg0: T) => void] => {
  const eventHandlers = useRef<{
    [key: string]: null | ((newTimestamp: T) => void);
  }>({});

  const onUpdate = useCallback((key: string, handler: null | ((newTimestamp: T) => void)) => {
    eventHandlers.current[key] = handler;
  }, []);

  const ref: RefState<T> = useRef({
    value: initialValue,
    onUpdate,
  });

  const setValue = useCallback((value: T) => {
    ref.current.value = value;
    Object.values(eventHandlers.current).forEach((handler) => handler?.(value));
  }, []);

  return [ref as RefState<T>, setValue];
};

export default useRefState;
